/**
 * src/components/PastBroadcastsTab.tsx
 *
 * "Past broadcasts" tab on a user's profile.  Shows the saved live
 * recordings the broadcaster opted to keep, with a built-in player
 * that replays the original Live Tools layer + gift toasts on top
 * of the video so the VOD looks identical to the original live.
 *
 * Three viewer modes co-exist:
 *   - Owner of the profile: full controls (pin, delete, replay).
 *   - Other connected wallet: replay only.
 *   - Anonymous viewer: replay only.
 *
 * The retention banner at the top warns the owner that anything
 * unpinned auto-purges after the retention window.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listBroadcasts,
  getBroadcastEvents,
  videoUrl,
  pinBroadcast,
  unpinBroadcast,
  deleteBroadcast,
  type SavedBroadcast,
  type RecordedEvent,
} from '../services/pastBroadcastsService';
import type { SigningWallet } from '../services/profileService';
import {
  dispatchLiveToolEvent,
  type LiveToolEvent,
} from '../services/liveToolsService';
import LiveToolsLayer from './LiveToolsLayer';
import type { LiveGiftEvent } from '../services/webrtcBroadcastService';

interface PastBroadcastsTabProps {
  /** Wallet whose profile is currently being viewed. */
  subjectWallet: string;
  /** Currently connected wallet (G-address) — drives owner-mode UI. */
  viewerWallet?: string | null;
  /** True iff `viewerWallet === subjectWallet`.  Pre-computed by the
   *  parent because the rest of ProfileOverlay already derives it. */
  isOwner: boolean;
  /** Signing capability for owner-only actions (pin / unpin / delete).
   *  Required when `isOwner` is true; the tab refuses to render owner
   *  controls without it.  This is the same `SigningWallet` the rest
   *  of ProfileOverlay uses for on-chain profile updates and gifts. */
  ownerSigner?: SigningWallet | null;
  /** Display name + avatar of the subject wallet's profile.  Used for
   *  the TikTok-style "@displayname" overlay during VOD playback.
   *  Both are optional — the modal falls back to a short G-address
   *  if either is missing. */
  subjectDisplayName?: string | null;
  subjectAvatarUrl?: string | null;
  /** Open another wallet's profile (used by the "🤖 by <creator>"
   *  link on agent broadcasts so viewers can jump to the human
   *  account that runs the agent). When omitted, the link is hidden. */
  onOpenWallet?: (wallet: string) => void;
}

const RETENTION_DAYS_HINT = 30;
const GIFT_TOAST_TTL_MS  = 6000; // matches live behaviour

const PastBroadcastsTab: React.FC<PastBroadcastsTabProps> = ({
  subjectWallet, viewerWallet, isOwner, ownerSigner,
  subjectDisplayName, subjectAvatarUrl, onOpenWallet,
}) => {
  const [recordings, setRecordings] = useState<SavedBroadcast[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  /**
   * `playingIdx` rather than `playing` so the replay modal can walk
   * forwards / backwards through the recordings list TikTok-style
   * without the parent having to look up the next/prev recording on
   * every navigation.  The list itself stays the same — we just
   * remap the index when the user swipes / taps a chevron / hits
   * arrow up/down.
   */
  const [playingIdx, setPlayingIdx] = useState<number | null>(null);
  /**
   * Direction of the most recent swipe — drives the slide-in
   * animation in the replay modal.  Cleared after the modal closes
   * so the next open uses the no-animation default.  We track it
   * here (not in the modal) because the animation must be triggered
   * BEFORE rec.id changes, which means the parent has to know which
   * way the user is going.
   */
  const [swipeDir, setSwipeDir] = useState<'up' | 'down' | null>(null);
  const playing = playingIdx != null && recordings ? (recordings[playingIdx] ?? null) : null;
  const goNext = useCallback(() => {
    setPlayingIdx((i) => {
      if (i == null || !recordings) return i;
      if (i >= recordings.length - 1) return i;
      setSwipeDir('up');
      return i + 1;
    });
  }, [recordings]);
  const goPrev = useCallback(() => {
    setPlayingIdx((i) => {
      if (i == null || !recordings) return i;
      if (i <= 0) return i;
      setSwipeDir('down');
      return i - 1;
    });
  }, [recordings]);

  // Owner controls (pin / delete) require both the wallet identity
  // and the ability to sign — without the signer the wallet kit
  // can't produce the SEP-10 challenge response, so we hide the
  // controls instead of showing buttons that always fail.
  const canManage = !!(isOwner && viewerWallet && ownerSigner);

  const refresh = useCallback(async () => {
    if (!subjectWallet) return;
    try {
      const list = await listBroadcasts(subjectWallet);
      setRecordings(list);
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'list_failed');
    }
  }, [subjectWallet]);

  useEffect(() => { refresh(); }, [refresh]);

  const handlePin = async (rec: SavedBroadcast) => {
    if (!canManage || !viewerWallet || !ownerSigner) return;
    setBusyId(rec.id);
    try {
      const updated = rec.pinned
        ? await unpinBroadcast(rec.id, viewerWallet, ownerSigner)
        : await pinBroadcast(rec.id, viewerWallet, ownerSigner);
      setRecordings((prev) => (prev || []).map((r) => (r.id === updated.id ? updated : r)));
    } catch (err: any) {
      setError(err?.message || 'pin_failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (rec: SavedBroadcast) => {
    if (!canManage || !viewerWallet || !ownerSigner) return;
    if (!window.confirm(`Delete this broadcast?\n\n${rec.title || rec.id} · ${formatDuration(rec.durationSec)}`)) return;
    setBusyId(rec.id);
    try {
      await deleteBroadcast(rec.id, viewerWallet, ownerSigner);
      setRecordings((prev) => (prev || []).filter((r) => r.id !== rec.id));
    } catch (err: any) {
      setError(err?.message || 'delete_failed');
    } finally {
      setBusyId(null);
    }
  };

  if (!subjectWallet) {
    return <div style={{ fontSize: 13, color: '#6b7280' }}>No wallet selected.</div>;
  }

  return (
    <div>
      {isOwner && (
        <div style={hintStyle}>
          Recordings are kept for {RETENTION_DAYS_HINT} days. Pin one with the ★
          button to keep it longer. Once your slot count is full the oldest
          unpinned recording is evicted to make room for a new one.
        </div>
      )}
      {isOwner && !ownerSigner && (
        <div style={warnStyle}>
          Connect your wallet to pin or delete recordings — the server requires
          a Stellar signature to confirm ownership.
        </div>
      )}

      {error && <div style={errStyle}>{prettyError(error)}</div>}

      {!recordings && !error && (
        <div style={{ fontSize: 13, color: '#6b7280' }}>Loading…</div>
      )}

      {recordings && recordings.length === 0 && (
        <div style={emptyStyle}>
          {isOwner
            ? 'No saved broadcasts yet. Toggle "Save broadcast to my profile" before going live to keep a copy here.'
            : 'This profile has no saved broadcasts yet.'}
        </div>
      )}

      {recordings && recordings.length > 0 && (
        <div style={gridStyle}>
          {recordings.map((rec, idx) => (
            <BroadcastCard
              key={rec.id}
              rec={rec}
              canManage={canManage}
              busy={busyId === rec.id}
              onPlay={() => setPlayingIdx(idx)}
              onTogglePin={() => handlePin(rec)}
              onDelete={() => handleDelete(rec)}
              onOpenCreator={
                onOpenWallet && rec.creatorWallet && rec.creatorWallet !== subjectWallet
                  ? () => onOpenWallet(rec.creatorWallet as string)
                  : undefined
              }
            />
          ))}
        </div>
      )}

      {playing && recordings && (
        <BroadcastReplayModal
          rec={playing}
          /* TikTok-style up/down navigation through the list.  We
             pass the immediate neighbours (and the count for the
             "1 of N" pill) rather than the full list so the modal
             stays small.  Swipe up / arrow-down → next; swipe
             down / arrow-up → previous. */
          totalCount={recordings.length}
          currentIdx={playingIdx ?? 0}
          onNext={(playingIdx ?? 0) < recordings.length - 1 ? goNext : undefined}
          onPrev={(playingIdx ?? 0) > 0 ? goPrev : undefined}
          nextTitle={recordings[(playingIdx ?? 0) + 1]?.title || null}
          prevTitle={recordings[(playingIdx ?? 0) - 1]?.title || null}
          /* TikTok-style profile overlay at the bottom of the
             stage.  The wallet's G-address fallback keeps the
             affordance present even before the profile loads. */
          subjectWallet={subjectWallet}
          subjectDisplayName={subjectDisplayName}
          subjectAvatarUrl={subjectAvatarUrl}
          /* Drives the slide-in animation each time `rec.id`
             changes.  Cleared on close (`null`) so the next open
             uses the no-animation default. */
          swipeDir={swipeDir}
          onClose={() => { setPlayingIdx(null); setSwipeDir(null); }}
        />
      )}
    </div>
  );
};

export default PastBroadcastsTab;

// ── Card ────────────────────────────────────────────────────────────

interface CardProps {
  rec: SavedBroadcast;
  /** True when the viewer is the owner *and* has a connected signer
   *  capable of producing the SEP-10 challenge response.  When
   *  false the pin / delete buttons are hidden — anyone can still
   *  play the recording, just can't manage it. */
  canManage: boolean;
  busy: boolean;
  onPlay: () => void;
  onTogglePin: () => void;
  onDelete: () => void;
  /** Jump to the creator's profile (agent broadcasts only). */
  onOpenCreator?: () => void;
}

const BroadcastCard: React.FC<CardProps> = ({
  rec, canManage, busy, onPlay, onTogglePin, onDelete, onOpenCreator,
}) => {
  const date = new Date(rec.startedAt).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
  return (
    <div style={cardStyle}>
      <button onClick={onPlay} style={cardThumbStyle} aria-label="Play recording">
        <div style={cardThumbInnerStyle}>
          <span style={playGlyphStyle}>▶</span>
          <span style={cardDurationStyle}>{formatDuration(rec.durationSec)}</span>
        </div>
      </button>
      <div style={{ padding: '10px 12px' }}>
        <div style={cardTitleStyle}>{rec.title || 'Untitled broadcast'}</div>
        <div style={cardMetaStyle}>
          {date} · {formatBytes(rec.fileBytes)}
          {rec.viewCount > 0 && <> · {rec.viewCount} {rec.viewCount === 1 ? 'view' : 'views'}</>}
          {rec.pinned && <> · ★ pinned</>}
        </div>
        {onOpenCreator && (
          <button
            type="button"
            onClick={onOpenCreator}
            style={creatorLinkStyle}
            title="Open the account that runs this agent"
          >
            🤖 by {shortWallet(rec.creatorWallet || '')} ↗
          </button>
        )}
        {canManage && (
          <div style={cardActionsStyle}>
            <button
              type="button"
              onClick={onTogglePin}
              disabled={busy}
              style={btnSecondary}
              title={rec.pinned ? 'Unpin (will auto-purge after retention)' : 'Pin (keep forever)'}
            >
              {busy ? '…' : rec.pinned ? '☆ Unpin' : '★ Pin'}
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={busy}
              style={btnDanger}
            >
              {busy ? '…' : 'Delete'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Replay modal ────────────────────────────────────────────────────

const BroadcastReplayModal: React.FC<{
  rec: SavedBroadcast;
  /** TikTok-style navigation.  Set when there's a recording above /
   *  below the current one in the parent's list.  When undefined the
   *  matching swipe direction / chevron / keyboard binding becomes
   *  a no-op (instead of wrapping around — most users find wrap-
   *  around disorienting). */
  onNext?: () => void;
  onPrev?: () => void;
  /** Hover labels for the chevrons + accessibility hints.  Optional;
   *  fall back to "Next / Previous" when the title is missing. */
  nextTitle?: string | null;
  prevTitle?: string | null;
  /** Position pill ("3 of 12") — shown discreetly at the top of the
   *  stage so the viewer always knows where they are in the list. */
  totalCount?: number;
  currentIdx?: number;
  /** Subject profile fields for the bottom-left overlay.  All three
   *  are optional; the modal falls back to a short G-address +
   *  initials avatar when the profile hasn't loaded yet. */
  subjectWallet?: string;
  subjectDisplayName?: string | null;
  subjectAvatarUrl?: string | null;
  /** Direction the most recent navigation went.  Drives the
   *  slide-in animation: 'up' (user swiped up / hit ↓) animates
   *  the new video in from the bottom; 'down' from the top.
   *  `null` means no animation (cold open). */
  swipeDir?: 'up' | 'down' | null;
  onClose: () => void;
}> = ({
  rec, onNext, onPrev, nextTitle, prevTitle, totalCount, currentIdx,
  subjectWallet, subjectDisplayName, subjectAvatarUrl,
  swipeDir, onClose,
}) => {
  const [events, setEvents] = useState<RecordedEvent[]>([]);
  // Local gift-toast queue.  Each replayed gift gets pushed in,
  // then auto-dismissed after `GIFT_TOAST_TTL_MS`.  We keep this
  // self-contained inside the modal so the broadcaster's live
  // toast stack (rendered by ProfileOverlay) is untouched.
  const [giftToasts, setGiftToasts] = useState<Array<LiveGiftEvent & { _ttlId: number }>>([]);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const stageRef = React.useRef<HTMLDivElement | null>(null);
  const cursorRef = React.useRef<number>(0);
  const [isFs, setIsFs] = React.useState<boolean>(false);

  /**
   * Fullscreen toggle that targets the **stage container** (the
   * `<div>` that holds <video> + LiveToolsLayer + ReplayGiftToastStack)
   * rather than the bare <video> element.  Why it matters:
   *
   * The replay modal renders the LiveToolsLayer as a sibling of the
   * <video>, absolutely positioned over it.  When the user clicks the
   * browser's native fullscreen button on `<video controls>`, only
   * the video element enters fullscreen — the overlay layer is left
   * behind in the document flow and disappears for the duration of
   * fullscreen playback.
   *
   * Fullscreening the container instead means the layer (and the
   * replay gift toast stack) come along for the ride, so the VOD
   * looks pixel-identical to what viewers saw in the original live
   * broadcast.  iOS Safari falls back to native video fullscreen
   * because it doesn't permit `requestFullscreen()` on arbitrary
   * elements before iOS 16.4 — there's nothing we can do about that
   * fallback short of building our own scrubber.
   */
  const enterFs = React.useCallback(async () => {
    const stage: any = stageRef.current;
    const video: any = videoRef.current;
    if (stage) {
      try {
        if (typeof stage.requestFullscreen === 'function') {
          await stage.requestFullscreen();
          return;
        }
        if (typeof stage.webkitRequestFullscreen === 'function') {
          stage.webkitRequestFullscreen();
          return;
        }
      } catch { /* fall through to video fallback */ }
    }
    if (video && typeof video.webkitEnterFullscreen === 'function') {
      try { video.webkitEnterFullscreen(); } catch { /* ignore */ }
    }
  }, []);
  const exitFs = React.useCallback(async () => {
    const d: any = document;
    try {
      if (typeof d.exitFullscreen === 'function') { await d.exitFullscreen(); return; }
      if (typeof d.webkitExitFullscreen === 'function') { d.webkitExitFullscreen(); return; }
    } catch { /* ignore */ }
    const video: any = videoRef.current;
    if (video && typeof video.webkitExitFullscreen === 'function') {
      try { video.webkitExitFullscreen(); } catch { /* ignore */ }
    }
  }, []);
  const toggleFs = React.useCallback(() => {
    if (isFs) void exitFs(); else void enterFs();
  }, [isFs, enterFs, exitFs]);

  // Track fullscreen state from both the standard `document`
  // event and the iOS-only per-video `webkitbeginfullscreen` /
  // `webkitendfullscreen` events.  Same pattern as the live
  // broadcaster's VideoPane in ProfileOverlay.
  useEffect(() => {
    const d: any = document;
    const sync = () => {
      const video: any = videoRef.current;
      const docFs = !!(d.fullscreenElement || d.webkitFullscreenElement);
      const iosFs = !!(video && video.webkitDisplayingFullscreen);
      setIsFs(docFs || iosFs);
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync as EventListener);
    const video = videoRef.current;
    if (video) {
      video.addEventListener('webkitbeginfullscreen', sync as EventListener);
      video.addEventListener('webkitendfullscreen', sync as EventListener);
    }
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync as EventListener);
      if (video) {
        video.removeEventListener('webkitbeginfullscreen', sync as EventListener);
        video.removeEventListener('webkitendfullscreen', sync as EventListener);
      }
    };
  }, []);

  // Synthetic replay session id keyed on the recording.  We rewrite
  // every dispatched event's sessionId to this so the LiveToolsLayer's
  // session filter accepts our replay events while ignoring whatever
  // is happening on actual live sessions in other tabs.
  const replaySessionId = useMemo(() => `replay:${rec.id}`, [rec.id]);

  useEffect(() => {
    if (!rec.hasEvents) { setEvents([]); return; }
    let cancelled = false;
    getBroadcastEvents(rec.id)
      .then((list) => {
        if (cancelled) return;
        const sorted = [...list].sort((a, b) => a.relativeMs - b.relativeMs);
        setEvents(sorted);
      })
      .catch(() => { if (!cancelled) setEvents([]); });
    return () => { cancelled = true; };
  }, [rec.id, rec.hasEvents]);

  // Replay engine.  Every `timeupdate` we advance through the events
  // log, dispatching anything that should have fired by now.  Seeking
  // backwards resets state; seeking forwards fast-forwards through
  // unfired events to land in a consistent state.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const fireToolEvent = (raw: LiveToolEvent) => {
      // Rewrite the sessionId so the LiveToolsLayer in this modal
      // (which is mounted with `replaySessionId`) accepts the event
      // through its session-filter.  The original sessionId is from
      // the live, which is long gone.
      const replayed: LiveToolEvent = { ...raw, sessionId: replaySessionId };
      try { dispatchLiveToolEvent(replayed); } catch { /* ignore */ }
    };

    // Seed the layer with an empty snapshot so the filter accepts
    // subsequent partial events even before any tool.added arrives.
    fireToolEvent({
      event: 'tool.snapshot',
      sessionId: replaySessionId,
      snapshot: { sessionId: replaySessionId, tools: [], capturedAt: new Date().toISOString() } as any,
    } as LiveToolEvent);

    const fireGiftEvent = (gift: LiveGiftEvent) => {
      // Tag each replayed gift with a TTL handle so we can auto-
      // dismiss it without disturbing earlier toasts.  Using a ref-
      // counted setTimeout keeps the toast stack stable even if
      // multiple gifts fire on the same `timeupdate` tick.
      const ttlId = Date.now() + Math.random();
      setGiftToasts((prev) => [...prev.slice(-5), { ...gift, _ttlId: ttlId }]);
      setTimeout(() => {
        setGiftToasts((prev) => prev.filter((t) => t._ttlId !== ttlId));
      }, GIFT_TOAST_TTL_MS);
    };

    const reset = () => {
      cursorRef.current = 0;
      // Clear tools by sending an empty snapshot.
      fireToolEvent({
        event: 'tool.snapshot',
        sessionId: replaySessionId,
        snapshot: { sessionId: replaySessionId, tools: [], capturedAt: new Date().toISOString() } as any,
      } as LiveToolEvent);
      // Clear stale gift toasts on backwards seeks so the player
      // never shows a gift that "hasn't happened yet".
      setGiftToasts([]);
    };

    const tick = () => {
      if (!video) return;
      const tMs = video.currentTime * 1000;
      while (cursorRef.current < events.length && events[cursorRef.current].relativeMs <= tMs) {
        const ev = events[cursorRef.current];
        if (ev.kind === 'tool') fireToolEvent(ev.payload as LiveToolEvent);
        else if (ev.kind === 'gift') fireGiftEvent(ev.payload as LiveGiftEvent);
        cursorRef.current += 1;
      }
    };

    const onSeeked = () => {
      const tMs = video.currentTime * 1000;
      const cursorTime = events[cursorRef.current - 1]?.relativeMs ?? -1;
      if (tMs < cursorTime) reset();
      tick();
    };

    video.addEventListener('timeupdate', tick);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('play', tick);
    return () => {
      video.removeEventListener('timeupdate', tick);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('play', tick);
    };
  }, [events, replaySessionId]);

  // Reset scrub + buffered events whenever the recording switches
  // (TikTok-style swipe to next/prev) so the new video starts from
  // the beginning instead of inheriting the previous video's
  // currentTime.  Without this the new video occasionally reuses the
  // last position because some browsers cache it on the same
  // <video> element.
  useEffect(() => {
    cursorRef.current = 0;
    setGiftToasts([]);
    const v = videoRef.current;
    if (v) {
      try { v.currentTime = 0; } catch { /* ignore */ }
      // Try to autoplay — most users will want the next video to
      // start without an extra tap.  Browsers that block autoplay
      // simply leave the play button visible; we don't surface an
      // error.
      v.play?.().catch(() => { /* ignore autoplay rejection */ });
    }
  }, [rec.id]);

  // ── TikTok-style vertical swipe + keyboard navigation ────────
  //
  // Mirror the pattern from the live VideoPane (in ProfileOverlay):
  // a sufficiently-vertical drag fires onNext (swipe up) or onPrev
  // (swipe down).  Horizontal drags are deliberately ignored so the
  // browser's swipe-back / scrubber-drag gestures keep working
  // inside the video.  Arrow up / arrow down work as keyboard
  // equivalents for desktop users.
  const swipeRef = React.useRef<{ x: number; y: number; t: number } | null>(null);
  const SWIPE_DIST_PX     = 60;
  const SWIPE_MAX_HOR     = 0.6; // horizontal must stay ≤ 60% of vertical
  const SWIPE_MAX_MS      = 700;
  const onTouchStart = (e: React.TouchEvent) => {
    if (!onNext && !onPrev) return;
    const t = e.touches[0];
    if (!t) return;
    swipeRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const s = swipeRef.current;
    swipeRef.current = null;
    const t = e.changedTouches[0];
    if (!s || !t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    const dt = Date.now() - s.t;
    if (dt > SWIPE_MAX_MS) return;
    if (Math.abs(dy) < SWIPE_DIST_PX) return;
    if (Math.abs(dx) > Math.abs(dy) * SWIPE_MAX_HOR) return;
    if (dy < 0) onNext?.();
    else        onPrev?.();
  };
  // Arrow up / arrow down for desktop.  Captured at document level
  // because focus inside a <video controls> normally consumes the
  // events before they bubble up to the modal container.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      // Ignore key repeats (holding the key shouldn't auto-advance
      // through the entire recording list).
      if (ev.repeat) return;
      // Don't hijack arrow keys while the user is typing in an
      // input field that the modal might gain in the future.
      const tgt = ev.target as HTMLElement | null;
      if (tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA')) return;
      if (ev.key === 'ArrowDown') {
        if (onNext) { ev.preventDefault(); onNext(); }
      } else if (ev.key === 'ArrowUp') {
        if (onPrev) { ev.preventDefault(); onPrev(); }
      } else if (ev.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onNext, onPrev, onClose]);

  return (
    <div style={modalBackdropStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              {rec.title || 'Untitled broadcast'}
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>
              {new Date(rec.startedAt).toLocaleString()} · {formatDuration(rec.durationSec)}
            </div>
          </div>
          <button onClick={onClose} style={closeBtnStyle} aria-label="Close">✕</button>
        </div>
        <div
          ref={stageRef}
          style={replayStageStyle}
          /* Double-click anywhere on the stage toggles fullscreen on
             the *container*, so the LiveToolsLayer sibling fullscreens
             with the video.  Without this, native <video> fullscreen
             promotes only the video element and the overlay vanishes. */
          onDoubleClick={toggleFs}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {/* Animated content layer.  The stage itself stays mounted
              (so the fullscreen ref + listeners survive across
              navigation) but this inner wrapper remounts on every
              `rec.id` change, which retriggers the CSS keyframe
              animation tied to its initial mount.  Picking the slide
              direction from the parent's `swipeDir` prop lets us
              animate next-up vs prev-down differently — TikTok-style. */}
          <div
            key={`stage-content-${rec.id}`}
            className={
              swipeDir === 'up'   ? 'replay-stage-content replay-slide-up'   :
              swipeDir === 'down' ? 'replay-stage-content replay-slide-down' :
                                    'replay-stage-content'
            }
            style={replayStageContentStyle}
          >
            <video
              ref={videoRef}
              src={videoUrl(rec.id)}
              controls
              /* Disable the native fullscreen button — clicking it
                 would fullscreen the bare <video>, which strips the
                 overlay siblings out of view.  Users get our custom
                 fullscreen button below + double-click on the stage. */
              controlsList="nofullscreen"
              disablePictureInPicture
              playsInline
              preload="metadata"
              style={replayVideoStyle}
            />
            {/* Reuse the same overlay we use for live broadcasts so the
                VOD looks pixel-identical to the original.  Mode is
                "viewer" so hidden tools stay hidden — no reason for a
                VOD to show queued cards. */}
            <LiveToolsLayer sessionId={replaySessionId} mode="viewer" />
            {/* Re-rendered gift toasts in sync with the recorded
                timeline.  Self-contained so the live broadcaster's
                real-time toast stack on the parent page is untouched. */}
            <ReplayGiftToastStack toasts={giftToasts} />
          </div>
          {/* Custom fullscreen toggle.  Targets the stage container
              so the overlay siblings come along.  Positioned away
              from the native controls (top-right) so it stays
              reachable while the user scrubs. */}
          <button
            type="button"
            onClick={toggleFs}
            aria-label={isFs ? 'Exit fullscreen' : 'Enter fullscreen'}
            title={isFs ? 'Exit fullscreen (Esc)' : 'Fullscreen — keeps live tools visible'}
            style={replayFsBtnStyle}
          >
            {isFs ? '⤡' : '⤢'}
          </button>

          {/* TikTok / Twitter-X-style profile overlay at the bottom-
              left of the stage.  Avatar + display name + recording
              title stack vertically so the broadcaster gets a
              persistent attribution overlay during VOD playback —
              identical to what viewers see on TikTok.  When the
              parent only loaded a wallet (profile fetch still in
              flight) we degrade gracefully to a short G-address
              and a 2-letter avatar fallback. */}
          {(subjectDisplayName || subjectWallet) && (
            <div style={replayProfileOverlayStyle} className="replay-profile-overlay">
              {subjectAvatarUrl ? (
                <img
                  src={subjectAvatarUrl}
                  alt=""
                  style={replayProfileAvatarStyle}
                  draggable={false}
                />
              ) : (
                <div style={replayProfileAvatarStyle as React.CSSProperties}>
                  {(subjectDisplayName || subjectWallet || '??')
                    .replace(/^@/, '')
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
              )}
              <div style={replayProfileTextStyle}>
                <div style={replayProfileNameStyle}>
                  @{subjectDisplayName || (subjectWallet ? shortAddrLocal(subjectWallet) : '')}
                </div>
                <div style={replayProfileTitleStyle}>
                  {rec.title || 'Untitled broadcast'}
                </div>
              </div>
            </div>
          )}

          {/* Discreet position pill — keep so the viewer knows where
              they are in the list, but render it small + transparent
              at the top so the new profile overlay owns the bottom-
              left. */}
          {totalCount && totalCount > 1 && currentIdx != null && (
            <div style={replayIndexPillStyle} aria-hidden>
              {currentIdx + 1} / {totalCount}
            </div>
          )}

          {/* Up / down chevrons for desktop (or anyone who'd rather
              click than swipe).  Hidden when the corresponding
              direction has no neighbour — no point showing a button
              that does nothing.  Positioned bottom-right so they sit
              next to the fullscreen button without crowding the
              native scrubber. */}
          {(onPrev || onNext) && (
            <div style={replayNavStackStyle}>
              <button
                type="button"
                onClick={() => onPrev?.()}
                disabled={!onPrev}
                style={{
                  ...replayNavBtnStyle,
                  opacity: onPrev ? 1 : 0.4,
                  cursor: onPrev ? 'pointer' : 'not-allowed',
                }}
                title={onPrev ? `Previous: ${prevTitle || 'recording'} (↑ / swipe down)` : 'No newer recording'}
                aria-label="Previous recording"
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => onNext?.()}
                disabled={!onNext}
                style={{
                  ...replayNavBtnStyle,
                  opacity: onNext ? 1 : 0.4,
                  cursor: onNext ? 'pointer' : 'not-allowed',
                }}
                title={onNext ? `Next: ${nextTitle || 'recording'} (↓ / swipe up)` : 'No older recording'}
                aria-label="Next recording"
              >
                ▼
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Helpers ─────────────────────────────────────────────────────────

function formatDuration(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m === 0) return `${r}s`;
  if (m < 60) return `${m}m ${r.toString().padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${(m % 60).toString().padStart(2, '0')}m`;
}

function shortWallet(w: string): string {
  if (!w) return '—';
  return w.length > 12 ? `${w.slice(0, 4)}…${w.slice(-4)}` : w;
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function prettyError(code: string): string {
  switch (code) {
    case 'list_failed':                 return 'Could not load past broadcasts. Try again in a moment.';
    case 'pin_failed':                  return 'Could not update pin status.';
    case 'delete_failed':               return 'Could not delete the recording.';
    case 'not_owner':                   return 'You can only manage broadcasts you originally recorded.';
    case 'quota_exceeded':              return 'You\'re at your saved-broadcast limit and every slot is pinned. Unpin or delete one to record another.';
    // Signature-challenge failures — surfaced when the wallet
    // refused, mis-signed, or took too long to sign.
    case 'challenge_failed':            return 'Could not request an ownership challenge from the server.';
    case 'challenge_not_found':
    case 'challenge_expired':           return 'Your signature challenge expired. Try again.';
    case 'challenge_already_used':      return 'That signature has already been used. Try the action again.';
    case 'signature_invalid':
    case 'wallet_header_mismatch':      return 'The wallet that signed doesn\'t match this recording\'s owner.';
    case 'signature_unreadable':        return 'The server couldn\'t read the signature format your wallet produced. Please report this — retrying won\'t help.';
    case 'malformed_envelope':
    case 'manage_data_op_missing':
    case 'nonce_mismatch':
    case 'source_mismatch':             return 'The signed challenge was rejected by the server. Reload the page and try again.';
    case 'missing_signed_challenge':    return 'Your wallet didn\'t finish signing the challenge.';
    default:                            return code.replaceAll('_', ' ');
  }
}

// ── Replay-only gift toast stack ────────────────────────────────────
//
// Self-contained rendering for replay so we don't have to thread the
// live ProfileOverlay's toast bus through the modal.  Visually
// matches the live `GiftToastLayer` (same yellow gradient, slide-in,
// truncated wallet) so the VOD experience feels identical.
const ReplayGiftToastStack: React.FC<{
  toasts: Array<LiveGiftEvent & { _ttlId: number }>;
}> = ({ toasts }) => {
  if (!toasts.length) return null;
  return (
    <div style={giftStackStyle} aria-live="polite" aria-label="Gifts (replay)">
      {toasts.map((g) => (
        <div key={g._ttlId} style={giftToastStyle}>
          <img
            src="/images/stellar-xlm.png"
            alt=""
            aria-hidden
            width={22}
            height={22}
            style={{ display: 'inline-block' }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <div style={giftTitleStyle}>
              {g.fromWallet ? `${g.fromWallet.slice(0, 6)}…${g.fromWallet.slice(-4)}` : 'Someone'}
              {' '}sent{' '}
              <strong>{g.amount} {g.assetCode}</strong>
            </div>
            {g.memo && <div style={giftMemoStyle}>"{g.memo}"</div>}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Styles ──────────────────────────────────────────────────────────

const hintStyle: React.CSSProperties = {
  fontSize: 12, color: '#6b7280',
  background: '#f8fafc', border: '1px solid #e5e7eb',
  borderRadius: 8, padding: '8px 10px', marginBottom: 10,
};
const warnStyle: React.CSSProperties = {
  fontSize: 12, color: '#92400e',
  background: '#fffbeb', border: '1px solid #fde68a',
  borderRadius: 8, padding: '8px 10px', marginBottom: 10,
};
const errStyle: React.CSSProperties = {
  fontSize: 12, color: '#b91c1c',
  background: '#fef2f2', border: '1px solid #fecaca',
  borderRadius: 8, padding: '8px 10px', marginBottom: 10,
};
const emptyStyle: React.CSSProperties = {
  fontSize: 13, color: '#6b7280',
  padding: '24px 12px', textAlign: 'center',
  background: '#f9fafb', border: '1px dashed #e5e7eb',
  borderRadius: 8,
};
const gridStyle: React.CSSProperties = {
  display: 'grid', gap: 12,
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
};
const cardStyle: React.CSSProperties = {
  background: '#fff', border: '1px solid #e5e7eb',
  borderRadius: 10, overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};
const cardThumbStyle: React.CSSProperties = {
  position: 'relative', width: '100%', aspectRatio: '16/9',
  background: 'linear-gradient(135deg, #1e293b, #0f172a)',
  border: 'none', cursor: 'pointer', padding: 0,
};
const cardThumbInnerStyle: React.CSSProperties = {
  position: 'absolute', inset: 0,
  display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center',
  color: '#fff', gap: 4,
};
const playGlyphStyle: React.CSSProperties = {
  width: 44, height: 44, borderRadius: '50%',
  background: 'rgba(255,255,255,0.15)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 18, lineHeight: 1, paddingLeft: 3,
};
const cardDurationStyle: React.CSSProperties = {
  fontSize: 11, opacity: 0.85,
  background: 'rgba(0,0,0,0.45)',
  padding: '2px 8px', borderRadius: 999,
};
const cardTitleStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: '#111827',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};
const cardMetaStyle: React.CSSProperties = {
  fontSize: 11, color: '#6b7280', marginTop: 4,
};
const cardActionsStyle: React.CSSProperties = {
  display: 'flex', gap: 6, marginTop: 8,
};
const creatorLinkStyle: React.CSSProperties = {
  marginTop: 6, padding: 0, background: 'none', border: 'none',
  color: '#6366f1', fontSize: 11, fontWeight: 600, cursor: 'pointer',
  textAlign: 'left',
};
const btnSecondary: React.CSSProperties = {
  fontSize: 12, padding: '4px 10px',
  background: '#f1f5f9', color: '#0f172a',
  border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer',
};
const btnDanger: React.CSSProperties = {
  fontSize: 12, padding: '4px 10px',
  background: '#fee2e2', color: '#991b1b',
  border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer',
};
const modalBackdropStyle: React.CSSProperties = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
  zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 16,
};
const modalStyle: React.CSSProperties = {
  background: '#0f172a', color: '#f8fafc',
  borderRadius: 12, overflow: 'hidden',
  width: 'min(960px, 100%)', maxHeight: '90vh',
  display: 'flex', flexDirection: 'column',
  border: '1px solid rgba(255,255,255,0.1)',
};
const modalHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)',
};
const closeBtnStyle: React.CSSProperties = {
  width: 32, height: 32, borderRadius: 8,
  background: 'rgba(255,255,255,0.08)', color: '#f4f6fa',
  border: 'none', cursor: 'pointer', fontSize: 16,
};
const replayStageStyle: React.CSSProperties = {
  position: 'relative', width: '100%',
  aspectRatio: '16/9', background: '#000',
  /* Hide overflow so the slide-in animation doesn't briefly paint
     adjacent recordings outside the stage's bounds while the
     keyframe is interpolating.  The fullscreen pseudo overrides
     this trivially since fullscreen elements always clip to viewport
     anyway. */
  overflow: 'hidden',
};
/** Inner wrapper that hosts <video> + LiveToolsLayer + gift toasts.
 *  Keyed by rec.id so it remounts on every navigation, retriggering
 *  whichever slide-in keyframe is active. */
const replayStageContentStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
};
/** Pin the <video> at z-index 0 so the absolutely-positioned overlay
 *  layer (z=18) and gift stack (z=21) render unambiguously above —
 *  some browsers promote a <video> in fullscreen to its own
 *  compositor layer, which can occlude siblings even with higher
 *  z-index values in the document.  Same defensive pattern we use
 *  in the live VideoPane in ProfileOverlay. */
const replayVideoStyle: React.CSSProperties = {
  width: '100%', height: '100%', objectFit: 'contain', background: '#000',
  position: 'relative', zIndex: 0,
};
const replayFsBtnStyle: React.CSSProperties = {
  position: 'absolute',
  top: 8, right: 8,
  width: 36, height: 36,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  borderRadius: 8,
  background: 'rgba(15, 18, 25, 0.7)',
  color: '#f4f6fa',
  border: '1px solid rgba(255,255,255,0.18)',
  cursor: 'pointer',
  fontSize: 18, lineHeight: 1,
  // Keep this above the LiveToolsLayer (z=18) and the gift stack
  // (z=21) so the broadcaster's tools never cover their own
  // fullscreen control during replay.
  zIndex: 25,
  pointerEvents: 'auto',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
};
/** Vertical stack of up / down chevrons for prev / next navigation,
 *  positioned bottom-right of the stage so the native scrubber stays
 *  visible.  Same z-index as the fullscreen button so they sit on
 *  the same compositor layer. */
const replayNavStackStyle: React.CSSProperties = {
  position: 'absolute',
  right: 8, bottom: 60, // sit above the native <video> control bar
  display: 'flex', flexDirection: 'column', gap: 6,
  zIndex: 25, pointerEvents: 'auto',
};
const replayNavBtnStyle: React.CSSProperties = {
  width: 40, height: 40,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  borderRadius: '50%',
  background: 'rgba(15, 18, 25, 0.7)',
  color: '#f4f6fa',
  border: '1px solid rgba(255,255,255,0.18)',
  fontSize: 14, lineHeight: 1, fontWeight: 700,
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
};
const replayIndexPillStyle: React.CSSProperties = {
  position: 'absolute',
  // Top-center so it doesn't fight the fullscreen button (top-right)
  // and leaves the bottom-left for the TikTok-style profile overlay.
  top: 8, left: '50%', transform: 'translateX(-50%)',
  padding: '3px 9px',
  borderRadius: 999,
  background: 'rgba(15, 18, 25, 0.55)',
  color: '#e5e7eb',
  fontSize: 11, fontWeight: 600,
  letterSpacing: 0.04,
  zIndex: 25,
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
};

/** TikTok / Twitter-X-style attribution overlay.  Sits at the bottom-
 *  left of the stage on top of the dark video gradient.  Uses
 *  pointer-events: none so it never absorbs clicks meant for the
 *  native scrubber sitting above it (the native controls float
 *  inside <video>'s shadow root and capture pointers there). */
const replayProfileOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  left: 12, bottom: 12,
  display: 'flex', alignItems: 'center', gap: 10,
  maxWidth: 'calc(100% - 110px)', // leave room for chevron stack at right
  padding: '6px 10px',
  borderRadius: 12,
  background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.7) 100%)',
  pointerEvents: 'none',
  zIndex: 22,
};
const replayProfileAvatarStyle: React.CSSProperties = {
  width: 36, height: 36, borderRadius: '50%',
  border: '2px solid rgba(255,255,255,0.35)',
  background: 'linear-gradient(135deg, #6366f1, #ec4899)',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  color: '#fff', fontSize: 13, fontWeight: 700, letterSpacing: 0.04,
  flexShrink: 0,
  objectFit: 'cover',
  // Tiny shadow so the avatar doesn't merge with a black video
  // background while the recording is loading.
  boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
};
const replayProfileTextStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', minWidth: 0,
  textShadow: '0 1px 2px rgba(0,0,0,0.7)',
};
const replayProfileNameStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 700, color: '#fff',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};
const replayProfileTitleStyle: React.CSSProperties = {
  fontSize: 11, color: 'rgba(255,255,255,0.85)',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};

/** Local helper — duplicated from ProfileOverlay to avoid circular
 *  import; same shape (`G123…ABCD`). */
function shortAddrLocal(addr: string): string {
  if (!addr) return '';
  return addr.length > 12 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr;
}
const giftStackStyle: React.CSSProperties = {
  position: 'absolute', top: 12, right: 12, zIndex: 21,
  display: 'flex', flexDirection: 'column', gap: 8,
  pointerEvents: 'none', maxWidth: 320,
};
const giftToastStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 10,
  padding: '10px 14px', borderRadius: 10,
  background: 'linear-gradient(135deg, #fde68a 0%, #fef3c7 55%, #ffffff 100%)',
  color: '#1f2937',
  border: '1px solid rgba(234, 179, 8, 0.55)',
  boxShadow: '0 8px 24px rgba(15,23,42,0.25)',
  fontSize: 13,
};
const giftTitleStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, lineHeight: 1.25,
};
const giftMemoStyle: React.CSSProperties = {
  fontSize: 12, fontStyle: 'italic', opacity: 0.9, lineHeight: 1.2,
};
